import React, { useState, useEffect } from 'react';
import { database, ref, get } from '../../firebase';
import { 
  Battery, 
  Wallet, 
  RefreshCw,
  DollarSign,
  Users,
  Shield
} from 'lucide-react';

const AUSTRALIA_TIMEZONE = 'Asia/Dubai';

const EnergyWallet = () => {
  const [walletBalance, setWalletBalance] = useState(0);
  const [energyIncomes, setEnergyIncomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextEnergyCredit, setNextEnergyCredit] = useState('');
  const [stats, setStats] = useState({
    totalEnergy: 0,
    todayEnergy: 0,
    blockedEnergy: 0,
    activeUplines: 0
  });

  const userDbKey = localStorage.getItem('dbKey');
  const userRegistrationDate = localStorage.getItem('registrationDate');

  useEffect(() => {
    if (userDbKey) {
      fetchEnergyWallet();
    }
  }, [userDbKey]);

  const getDateKeyInTimezone = (date, timezone = AUSTRALIA_TIMEZONE) => {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(date);
  };

  const parseDateKeyToAESTRunDate = (dateKey) => {
    const [year, month, day] = (dateKey || '').split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(Date.UTC(year, month - 1, day, 0, 0, 0) - (4 * 60 * 60 * 1000));
  };

  const fetchEnergyWallet = async () => {
    try {
      setLoading(true);
      
      // Fetch user wallet balance
      if (userRegistrationDate && userDbKey) {
        const userRef = ref(database, `users/${userRegistrationDate}/${userDbKey}`);
        const userSnapshot = await get(userRef);
        
        if (userSnapshot.exists()) {
          const userData = userSnapshot.val();
          const energyBalance = parseFloat(userData.wallets?.energy) || 0;
          setWalletBalance(energyBalance);
        }
      }
      
      // Fetch energy wallet incomes
      const roiRef = ref(database, 'roiIncomes');
      const roiSnapshot = await get(roiRef);
      
      const incomes = [];
      let totalEnergy = 0;
      let todayEnergy = 0;
      let blockedEnergy = 0;
      let activeUplines = 0;
      const today = getDateKeyInTimezone(new Date(), AUSTRALIA_TIMEZONE);
      
      if (roiSnapshot.exists()) {
        const data = roiSnapshot.val();
        Object.entries(data).forEach(([date, users]) => {
          if (users[userDbKey]) {
            Object.entries(users[userDbKey]).forEach(([incomeId, income]) => {
              // Filter only energy wallet incomes
              if (income.type === 'energy_wallet') {
                const amount = parseFloat(income.amount) || 0;
                const isBlocked = income.status === 'blocked';
                const isUplineActive = income.isUplineActive || false;
                
                const energyItem = {
                  id: incomeId,
                  date: date,
                  amount: amount,
                  status: income.status || 'credited',
                  fromUser: income.fromUserName,
                  fromUserId: income.fromUserId,
                  packageName: income.packageName,
                  description: income.description,
                  timestamp: income.date || income.createdAt,
                  isBlocked: isBlocked,
                  isUplineActive: isUplineActive
                };
                
                incomes.push(energyItem);
                
                if (!isBlocked) {
                  totalEnergy += amount;
                  if (date === today) todayEnergy += amount;
                  if (isUplineActive) activeUplines++;
                } else {
                  blockedEnergy += amount;
                }
              }
            });
          }
        });
      }

      // Find next upcoming energy credit from ROI schedules
      let earliestNext = null;
      let hasEnergySchedule = false;
      try {
        const scheduleRef = ref(database, 'ROISchedule');
        const scheduleSnap = await get(scheduleRef);
        if (scheduleSnap.exists()) {
          const scheduleData = scheduleSnap.val();
          Object.entries(scheduleData).forEach(([dateKey, dateSchedules]) => {
            if (!dateSchedules) return;
            Object.entries(dateSchedules).forEach(([uid, userSchedules]) => {
              if (!userSchedules) return;
              Object.entries(userSchedules).forEach(([scheduleId, schedule]) => {
                const energyInfo = schedule?.energyWalletInfo;
                if (!energyInfo || !energyInfo.isActive) return;
                if (energyInfo.uplineDbKey !== userDbKey) return;

                hasEnergySchedule = true;
                const nextKey = energyInfo.nextEnergyCreditKey || schedule.nextRoiDateKey;
                const nextDate = nextKey ? parseDateKeyToAESTRunDate(nextKey) : (energyInfo.nextEnergyCredit ? new Date(energyInfo.nextEnergyCredit) : null);
                if (!nextDate || Number.isNaN(nextDate.getTime())) return;

                if (!earliestNext || nextDate < earliestNext) {
                  earliestNext = nextDate;
                }
              });
            });
          });
        }
      } catch (error) {
        console.error('Error fetching energy schedules:', error);
      }

      if (earliestNext) {
        const dateStr = earliestNext.toLocaleDateString('en-US', {
          timeZone: AUSTRALIA_TIMEZONE,
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
        const timeStr = earliestNext.toLocaleTimeString('en-US', {
          timeZone: AUSTRALIA_TIMEZONE,
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
        setNextEnergyCredit(`${dateStr} ${timeStr} (Dubai)`);
      } else {
        setNextEnergyCredit(
          hasEnergySchedule
            ? 'Awaiting next energy schedule'
            : 'No active downline ROI yet'
        );
      }
      
      // Sort by date (newest first)
      incomes.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      setEnergyIncomes(incomes);
      setStats({
        totalEnergy,
        todayEnergy,
        blockedEnergy,
        activeUplines
      });
    } catch (error) {
      console.error('Error fetching energy wallet:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      timeZone: AUSTRALIA_TIMEZONE,
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status, isUplineActive) => {
    if (status === 'blocked') {
      return <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-red-500/20 text-red-400 border border-red-500/30">
        <Shield className="h-3 w-3 mr-1" />
        Blocked
      </span>;
    }
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs ${
        isUplineActive 
          ? 'bg-green-500/20 text-green-400 border-green-500/30' 
          : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
      }`}>
        {isUplineActive ? 'Active Upline' : 'Inactive Upline'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4">
      <h1 className="text-2xl font-bold text-white mb-2">AV Energy Wallet</h1>
      <p className="text-gray-400 mb-6">Earnings from downline energy commissions</p>

      {/* Wallet Balance Card */}
      <div className="card-glow p-6 mb-6 bg-gradient-to-r from-purple-500/10 to-primary/10 border border-purple-500/30 rounded-xl">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white mb-2">Energy Wallet Balance</h3>
            <div className="text-4xl font-bold text-purple-400">{formatCurrency(walletBalance)}</div>
            <p className="text-gray-400 text-sm mt-2">Energy from downline ROI</p>
            <p className="text-gray-500 text-xs mt-1">Next Energy Credit: {nextEnergyCredit || 'N/A'}</p>
          </div>
          <div className="p-4 bg-purple-500/20 rounded-full">
            <Battery className="h-12 w-12 text-purple-400" />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Total Energy</div>
          <div className="text-lg font-bold text-white">{formatCurrency(stats.totalEnergy)}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Today's Energy</div>
          <div className="text-lg font-bold text-green-400">{formatCurrency(stats.todayEnergy)}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Active Uplines</div>
          <div className="text-lg font-bold text-blue-400">{stats.activeUplines}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Blocked</div>
          <div className="text-lg font-bold text-red-400">{formatCurrency(stats.blockedEnergy)}</div>
        </div>
      </div>

      {/* Refresh Button */}
      <div className="mb-6">
        <button
          onClick={fetchEnergyWallet}
          className="px-4 py-2 bg-primary hover:bg-primary/80 rounded-lg text-white flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Energy Incomes */}
      <div className="space-y-4">
        <h3 className="text-lg font-bold text-white mb-4">Energy Earnings</h3>
        
        {energyIncomes.length > 0 ? (
          energyIncomes.map((income) => {
            const isBlocked = income.status === 'blocked';
            
            return (
              <div key={income.id} className="card-glow p-4 bg-gray-800/50 rounded-xl">
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${isBlocked ? 'bg-red-500/20' : 'bg-purple-500/20'}`}>
                      <Battery className={`h-5 w-5 ${isBlocked ? 'text-red-400' : 'text-purple-400'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-purple-500/20 text-purple-400 border border-purple-500/30">
                          Energy Commission
                        </span>
                        {getStatusBadge(income.status, income.isUplineActive)}
                      </div>
                      <p className="text-gray-300 text-sm">{income.description}</p>
                      <p className="text-gray-400 text-xs mt-1">
                        From: {income.fromUser} • Package: {income.packageName} • {formatDate(income.timestamp)}
                      </p>
                      
                      {!isBlocked && (
                        <p className={`text-xs mt-1 ${income.isUplineActive ? 'text-green-400' : 'text-yellow-400'}`}>
                          {income.isUplineActive ? '✅ Active downline' : '⚠️ Inactive downline'}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className={`text-lg font-bold ${isBlocked ? 'text-red-400' : 'text-purple-400'}`}>
                    {isBlocked ? '-' : '+'}{formatCurrency(income.amount)}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-12 card-glow bg-gray-800/50 rounded-xl">
            <div className="text-4xl mb-4">📊</div>
            <h3 className="text-lg font-semibold text-white mb-2">No Energy Earnings</h3>
            <p className="text-gray-400">You haven't received any energy commissions yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnergyWallet;


